/**
 * Date helpers for the projection engine.
 *
 * Everything here works on `YYYY-MM-DD` strings and treats them as UTC midnight,
 * so results never shift with the machine's timezone. No function reads the
 * current clock — `today` is always an argument.
 */

import type { IsoDate, TimeUnit } from './types.js';

const MS_PER_DAY = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const d = new Date(value + 'T00:00:00Z');
  return !Number.isNaN(d.getTime()) && toIso(d) === value;
}

export function parseIso(value: IsoDate): Date {
  if (!isIsoDate(value)) {
    throw new TypeError(`Expected a YYYY-MM-DD date, got: ${JSON.stringify(value)}`);
  }
  return new Date(value + 'T00:00:00Z');
}

export function toIso(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = parseIso(date);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/**
 * Add calendar months, clamping to the end of the target month.
 * Jan 31 + 1 month is Feb 28 (or Feb 29 in a leap year), not Mar 3.
 */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const d = parseIso(date);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const lastDayOfMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDayOfMonth));
  return toIso(d);
}

export function addInterval(date: IsoDate, value: number, unit: TimeUnit): IsoDate {
  switch (unit) {
    case 'days':
      return addDays(date, value);
    case 'weeks':
      return addDays(date, value * 7);
    case 'months':
      return addMonths(date, value);
    default: {
      const exhaustive: never = unit;
      throw new TypeError(`Unsupported time unit: ${String(exhaustive)}`);
    }
  }
}

/** Whole days from `from` to `to`. Negative when `to` is in the past. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parseIso(to).getTime() - parseIso(from).getTime()) / MS_PER_DAY);
}

export function compareIso(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minIso(a: IsoDate, b: IsoDate): IsoDate {
  return a <= b ? a : b;
}

export function maxIso(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}
